import { Enquiry } from "../components/Enquiry";
import { Heading } from "../components/Heading/Heading";
import { Layout } from "../components/Layout";
import { DefaultSection } from "../components/Sections/DefaultSection";
import { SharedLayout } from "../components/Templates";
import { Toggle } from "../components/Toggle/Toggle";
import { VideoModal } from "../components/VideoModal";
import { getHostname } from "../services/Utils";
import axios from "axios";
import { format } from "date-fns";
import { graphql } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const HELMET_PROPS = {
  class: "body-shorthand body-marketing body-marketing-secondary body-marketing-training hide-elevio",
};

export const query = graphql`
  query {
    allOnDemandWebinarsJson {
      nodes {
        id
        title
        url
        alt
        imagePath {
          src {
            childImageSharp {
              gatsbyImageData(width: 400, height: 225, placeholder: BLURRED)
            }
          }
        }
      }
    }
  }
`;

interface OnDemandWebinarProps {
  webinar: {
    id: string;
    title: string;
    url: string;
    alt: string;
    imagePath: {
      src: {
        childImageSharp: {
          gatsbyImageData: any;
        };
      };
    };
  };
}

export default class TrainingView extends React.Component<
  { data: any },
  {
    isLoading: boolean;
    webinars: any;
    err: string;
    showPortal: boolean;
    activeTab: "upcoming" | "ondemand";
  }
> {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      webinars: null,
      err: null,
      showPortal: false,
      activeTab: "upcoming",
    };
    this.openContact = this.openContact.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
  }

  handleToggle(tab: "upcoming" | "ondemand") {
    this.setState({ activeTab: tab });
  }

  openContact() {
    this.setState(() => ({ showPortal: true }));
  }

  componentDidMount() {
    const uri = `${getHostname("api")}/webinars?hostId=MgsB4Kg5SOKysZmxxbBh0w&pageSize=6`;
    axios
      .get(uri)
      .then(res => {
        this.setState(() => ({ isLoading: false, webinars: res.data }));
      })
      .catch(err => {
        let message = "Webinars are not available at this time.";
        if (err && err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        } else if (err && err.message) {
          message = err.message;
        }
        this.setState(() => ({
          isLoading: false,
          webinars: null,
          err: message,
        }));
      });
  }

  render() {
    const { isLoading, webinars, err, activeTab } = this.state;
    const { allOnDemandWebinarsJson } = this.props.data;
    const onDemandWebinars = allOnDemandWebinarsJson.nodes;

    const portalProps = {
      type: "training-enquiry",
      subject: "training-enquiry",
      source: "marketing/training",
      onClose: () => this.setState(() => ({ showPortal: false })),
    };

    const hasWebinars = !isLoading && webinars && webinars.length > 0;

    return (
      <Layout location={this.props.location} theme={"dark-theme"}>
        <main id="main" className="marketing-content container-fluid container-training tw-bg-black tw-text-white">
          <Helmet bodyAttributes={HELMET_PROPS} />
          <DefaultSection>
            <StaticImage
              src="../../static/media/logos/logo-white.svg"
              alt="Shorthand logo"
              aria-hidden="true"
              placeholder="blurred"
              width={86}
              height={65}
              className="tw-mb-9 !tw-hidden tw-h-[65px] max-lg:!tw-block"
              objectFit="contain"
            />

            <Heading headerType={1}>
              Webinars <span className="tw-font-PassengerSerif tw-italic">&amp;</span> training
            </Heading>

            <Heading
              headerType={2}
              className="tw-w-full tw-pt-[18px] !tw-text-center !tw-text-2xl !tw-font-normal !tw-text-grey_scale-200 max-sm:!tw-w-full max-sm:!tw-text-xl"
            >
              Discover how to get the most out of Shorthand with demos, community events & inspiring examples or{" "}
              <button onClick={this.openContact} className="btn-link hover:!tw-text-white hover:!tw-opacity-100">
                click here
              </button>{" "}
              to chat with our team.
            </Heading>
            <div className="tw-mb-4 tw-mt-16">
              <Toggle activeTab={activeTab} onToggle={this.handleToggle} openContact={this.openContact} />
            </div>
          </DefaultSection>

          {this.state.showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
          <div className="row section-webinars justify-content-center">
            <div className="col-11 col-sm-10 mx-auto webinars">
              {activeTab === "upcoming" ? (
                <div role="tabpanel" id="upcoming-panel" aria-labelledby="upcoming-tab">
                  <div className="intro-text webinars-panel" />
                  {isLoading && (
                    <div className="webinars-panel error-text text-center">
                      <i className="fa fa-circle-notch fa-spin" aria-hidden="true" />
                    </div>
                  )}
                  {!isLoading && (!hasWebinars || err) && (
                    <div className="webinars-panel error-text text-center">
                      {err ? err : "There are currently no scheduled training sessions"}.{" "}
                      <button className="btn-link" onClick={this.openContact}>
                        Please contact us to discuss training.
                      </button>
                    </div>
                  )}
                  {hasWebinars && (
                    <div className="webinars-panel">
                      <ul id="webinars">
                        {webinars.map(webinar => (
                          <Webinar key={webinar.id} webinar={webinar} />
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div role="tabpanel" id="ondemand-panel" aria-labelledby="ondemand-tab" className="webinars-panel tw-animate-fadeIn">
                  <ul id="on-demand-webinars" className="tw-mx-auto tw-flex tw-flex-wrap tw-justify-center tw-gap-6">
                    {onDemandWebinars.map(webinar => (
                      <OnDemandWebinar key={webinar.id} webinar={webinar} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="tw-flex tw-justify-center tw-py-16">
            <a
              href="https://www.youtube.com/@Shorthand"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-inverse !tw-m-0 !tw-px-8 !tw-py-4 !tw-text-lg !tw-font-bold max-xl:!tw-text-base max-sm:!tw-text-sm"
            >
              Video library
            </a>
          </div>
        </main>
        <SharedLayout theme="dark-theme" />
      </Layout>
    );
  }
}

const Webinar = ({ webinar }) => (
  <li className="webinars-panel-item tw-animate-fadeIn">
    <div className="webinars-panel-time">
      <div className="webinars-panel-start">
        <i className="fa fa-calendar" aria-hidden="true" />
        {format(new Date(webinar.start_time), "MMM d, yyyy h:mm a")}
      </div>
      <div className="webinars-panel-duration">
        <i className="fa fa-clock-o" aria-hidden="true" />
        {webinar.duration} mins
      </div>
    </div>
    <div className="webinars-panel-inner">
      <h3 className="webinars-panel-title">{webinar.topic}</h3>

      <div className="webinars-panel-agenda">{webinar.agenda}</div>
      <a className="btn btn-primary" href={webinar.join_url} target="_blank" rel="noopener noreferrer">
        Register now
      </a>
    </div>
  </li>
);

const OnDemandWebinar: React.FC<OnDemandWebinarProps> = ({ webinar }) => {
  const [showVideoModal, setShowVideoModal] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const openVideoModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  return (
    <li>
      <button
        ref={buttonRef}
        className="lightBorder list-item-transition unstyled text-center tw-bg-transparent  focus-visible:tw-ring-opacity-1 tw-block !tw-w-[400px] tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-p-0 tw-text-left focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand_scale-500"
        onClick={openVideoModal}
      >
        <div className="featured-story-media">
          <GatsbyImage
            image={webinar.imagePath.src.childImageSharp.gatsbyImageData}
            alt={webinar.alt}
            className="list-item-transition tw-w-[100%]"
          />
        </div>
        <div className="featured-story-title tw-bg-white tw-bg-opacity-10 tw-py-6 tw-text-center !tw-text-white">
          <h4 className="tw-ml-4 tw-text-center !tw-text-[22px] !tw-font-bold tw-text-white">{webinar.title}</h4>
        </div>
      </button>
      {showVideoModal &&
        ReactDOM.createPortal(
          <VideoModal videoUrl={webinar.url} onClose={closeVideoModal} triggerRef={buttonRef} />,
          document.getElementById("modal")
        )}
    </li>
  );
};
